'use client';

import { useEffect, useState, useSyncExternalStore } from 'react';
import posthog from 'posthog-js';
import { tw } from '@tw';
import { kitDoor } from '@/lib/quiz/kit-copy';
import {
  addressProblem,
  claimedUsername,
  useAddressTyping,
  type ClaimAddressState,
} from './AddressField';

const noSubscribe = () => () => {};

// Segments a hairline apart so Edit and Copy, both card, stay two; below `deck`
// the address stands over the buttons. No `mm-btn`: a segment that scales on
// press shrinks away from the track and shows square corners.
const TRACK =
  'flex flex-col gap-2 deck:inline-flex deck:max-w-full deck:flex-row deck:gap-px deck:self-start deck:overflow-hidden deck:rounded-full deck:bg-[var(--sf-line)]';
const BUTTONS =
  'flex min-h-11 gap-px overflow-hidden rounded-full bg-[var(--sf-line)] deck:contents';
const SEG =
  'flex flex-1 items-center justify-center px-4 text-badge font-semibold transition-opacity active:opacity-80 deck:flex-none';
const ADDRESS =
  'flex min-h-11 min-w-0 items-center rounded-full bg-[var(--sf-card)] px-4 text-[16px] font-semibold text-[var(--sf-ink)] deck:rounded-none';
// One weight and width across the row; colour alone sets the domain apart.
const DOMAIN = 'text-[var(--sf-mut)]';
const PAPER = tw(SEG, 'bg-[var(--sf-card)] text-[var(--sf-ink)]');
// Quiet, like the Edit pill on every other editable thing on the page.
const EDIT = tw(PAPER, 'text-[var(--sf-mut)]');
// One button fits beside the address at any width, so it never gets a row of its own.
const ROW =
  'inline-flex max-w-full flex-row gap-px self-start overflow-hidden rounded-full bg-[var(--sf-line)]';
const INK = tw(SEG, 'min-w-[5.5rem] bg-[var(--sf-ink)] text-[var(--sf-page)]');

/**
 * The page's address under its name, one segmented pill: the address in white,
 * then Edit (the owner's, turning the white half into the field), Copy, and
 * Share in ink where the device has a share sheet. `live` false (the prebuilt
 * page) has nothing to copy or share yet.
 */
export function PageLink({
  username,
  own,
  live = true,
  onSave,
}: {
  username: string;
  /** The link already theirs: the one taken name that is free to them. */
  own?: string;
  live?: boolean;
  /** The owner's save. Resolves to an error to show, or null once the link is taken. */
  onSave?: (next: string) => Promise<string | null>;
}) {
  const [draft, setDraft] = useState<ClaimAddressState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const type = useAddressTyping(own, (next) => {
    setDraft(next);
    setError(null);
  });
  // False on the server, so the first paint matches hydration either way.
  const canShare = useSyncExternalStore(
    noSubscribe,
    () => typeof navigator.share === 'function',
    () => false
  );
  const url = `https://${kitDoor(username)}`;

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(t);
  }, [copied]);

  const track = (method: 'sheet' | 'link') =>
    posthog.capture('storefront_shared', {
      method,
      is_owner: !!onSave,
      creator_username: username,
      provider_username: username,
      tutor_username: username,
    });

  const share = async () => {
    try {
      await navigator.share({ url });
    } catch {
      return; // Dismissed sheet.
    }
    track('sheet');
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      return; // Denied clipboard: the address is on the page to select.
    }
    setCopied(true);
    track('link');
  };

  const close = () => {
    setDraft(null);
    setError(null);
  };

  const save = async () => {
    if (!draft || !onSave) return;
    const problem = addressProblem(draft);
    if (problem) return setError(problem);
    const next = claimedUsername(draft);
    if (next === username) return close();
    setSaving(true);
    const failed = await onSave(next);
    setSaving(false);
    if (failed) setError(failed);
    else close();
  };

  // Only what stops the save; a link still being checked says nothing yet.
  const blocker = draft && (error ?? (draft.status === 'checking' ? null : addressProblem(draft)));

  if (draft)
    return (
      <div className="flex flex-col gap-2">
        <div className={TRACK}>
          <label className={ADDRESS}>
            <span className={DOMAIN}>meetmy.io/</span>
            <input
              aria-label="Your link"
              autoFocus
              value={draft.username}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              onChange={(e) => type(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void save();
                if (e.key === 'Escape') close();
              }}
              size={Math.max(draft.username.length, 4)}
              className="min-w-0 flex-1 border-b-2 border-[var(--sf-ink)] bg-transparent outline-none deck:flex-none"
            />
          </label>
          <div className={BUTTONS}>
            <button type="button" onClick={close} className={PAPER}>
              Cancel
            </button>
            <button type="button" onClick={() => void save()} disabled={saving} className={INK}>
              Save
            </button>
          </div>
        </div>
        {blocker && (
          <span role="status" className="sf-note text-error">
            {blocker}
          </span>
        )}
      </div>
    );

  const one = [onSave, live, live && canShare].filter(Boolean).length <= 1;
  return (
    <div className={one ? ROW : TRACK}>
      <span className={tw(ADDRESS, one && 'rounded-none')}>
        <span className={tw(DOMAIN, 'shrink-0')}>meetmy.io/</span>
        <span className="truncate">{username}</span>
      </span>
      <div className={one ? 'contents' : BUTTONS}>
        {onSave && (
          <button
            type="button"
            onClick={() => setDraft({ username, status: 'free' })}
            className={tw(EDIT, one && 'flex-none')}
          >
            Edit
          </button>
        )}
        {live && (
          <button
            type="button"
            onClick={() => void copy()}
            className={canShare ? tw(PAPER, 'min-w-[5.5rem]') : INK}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        )}
        {live && canShare && (
          <button type="button" onClick={() => void share()} className={INK}>
            Share
          </button>
        )}
      </div>
    </div>
  );
}
